import { Widget } from './form-widget.model';

type EquipmentMetadata = {
  id?: string | number | null;
  equipment_type?: string;
  time?: number | null;
  quantity?: number | null;
  unit?: number | null;
  notes?: string;
  attributes?: string[];
};

export class FbEmrEquipmentWidget extends Widget {
  constructor(data?: unknown) {
    super(data);

    this.name = 'fb-emr-equipment';
    this.title = 'Equipment';

    this.properties.text ??= { title: 'Header', value: 'Equipment' };
  }

  public getMetadata(): Record<string, unknown> {
    if (this.properties?.items?.hidden) {
      return {};
    }

    const value = this.properties?.items?.value;
    const equipments = Array.isArray(value) ? this.buildEquipments(value) : [];

    if (equipments.length === 0) {
      return {};
    }

    return { equipments };
  }

  public isValid(): boolean {
    if (this.properties?.items?.hidden) {
      return true;
    }

    if (this.properties?.items?.formInvalid === false) {
      return true;
    }

    const items = Array.isArray(this.properties?.items?.value)
      ? (this.properties.items.value as EquipmentMetadata[])
      : [];
    if (items.length === 0) {
      return false;
    }
    const hasInvalidNumeric = items.some((item) => this.hasInvalidNumericValue(item));
    const hasMissingEquipmentType = items.some((item) => this.isEquipmentTypeMissing(item));
    return !hasInvalidNumeric && !hasMissingEquipmentType;
  }

  private buildEquipments(items: EquipmentMetadata[]): EquipmentMetadata[] {
    return items
      .map((item) => this.buildEquipment(item))
      .filter((entry) => this.hasAnyValue(entry))
      .map((entry) => this.applyNumericDefaults(entry));
  }

  private buildEquipment(item: EquipmentMetadata | null): EquipmentMetadata {
    const equipment: EquipmentMetadata = {};

    const id = this.normalizeId((item as any)?.id ?? (item as any)?.Id ?? null);
    if (id !== null) {
      equipment.id = id;
    }

    const equipmentType = this.asTrimmedString(
      (item as any)?.equipmentType ?? (item as any)?.EquipmentType,
    );
    equipment.equipment_type = equipmentType;

    equipment.time = this.parseInteger((item as any)?.time ?? (item as any)?.Time);
    equipment.quantity = this.parseInteger((item as any)?.quantity ?? (item as any)?.Quantity);
    equipment.unit = this.parseFloatValue((item as any)?.unit ?? (item as any)?.Unit);

    const notes = this.asTrimmedString((item as any)?.notes ?? (item as any)?.Notes);
    equipment.notes = notes;

    equipment.attributes = this.buildAttributes(item);

    return equipment;
  }

  private hasInvalidNumericValue(item: EquipmentMetadata | null): boolean {
    if (!item || typeof item !== 'object') {
      return false;
    }

    const time = (item as any)?.time ?? (item as any)?.Time ?? null;
    const quantity = (item as any)?.quantity ?? (item as any)?.Quantity ?? null;
    const unit = (item as any)?.unit ?? (item as any)?.Unit ?? null;

    return (
      !this.isNumericValue(time) || !this.isNumericValue(quantity) || !this.isNumericValue(unit)
    );
  }

  private isEquipmentTypeMissing(item: EquipmentMetadata | null): boolean {
    const equipmentType = this.getEquipmentType(item);
    return !this.hasValue(equipmentType);
  }

  private getEquipmentType(item: EquipmentMetadata | null): unknown {
    return (
      (item as any)?.equipmentType ??
      (item as any)?.EquipmentType ??
      (item as any)?.equipment_type ??
      null
    );
  }

  private hasNonTypeValue(item: EquipmentMetadata | null): boolean {
    if (!item || typeof item !== 'object') {
      return false;
    }

    const fields = [
      (item as any)?.time ?? (item as any)?.Time ?? null,
      (item as any)?.quantity ?? (item as any)?.Quantity ?? null,
      (item as any)?.unit ?? (item as any)?.Unit ?? null,
      (item as any)?.notes ?? (item as any)?.Notes ?? null,
      ...(Array.isArray((item as any)?.attributes) ? (item as any).attributes : []),
      ...(Array.isArray((item as any)?.Attributes) ? (item as any).Attributes : []),
      (item as any)?.attribute1 ?? (item as any)?.Attribute1 ?? null,
      (item as any)?.attribute2 ?? (item as any)?.Attribute2 ?? null,
      (item as any)?.attribute3 ?? (item as any)?.Attribute3 ?? null,
      (item as any)?.attribute4 ?? (item as any)?.Attribute4 ?? null,
      (item as any)?.attribute5 ?? (item as any)?.Attribute5 ?? null,
      (item as any)?.attribute6 ?? (item as any)?.Attribute6 ?? null,
      (item as any)?.attribute7 ?? (item as any)?.Attribute7 ?? null,
      (item as any)?.attribute8 ?? (item as any)?.Attribute8 ?? null,
      (item as any)?.attribute9 ?? (item as any)?.Attribute9 ?? null,
      (item as any)?.attribute10 ?? (item as any)?.Attribute10 ?? null,
    ];

    return fields.some((value) => this.hasValue(value));
  }

  private buildAttributes(item: EquipmentMetadata | null): string[] {
    const attributes: string[] = [];

    for (let i = 1; i <= 10; i++) {
      const key = `attribute${i}`;
      const legacyKey = `Attribute${i}`;
      const value = this.asTrimmedString((item as any)?.[key] ?? (item as any)?.[legacyKey]);
      attributes.push(value);
    }

    return attributes;
  }

  private hasAnyValue(entry: EquipmentMetadata): boolean {
    return Object.entries(entry).some(([key, value]) => {
      if (key === 'attributes') {
        return Array.isArray(value) && value.some((item) => this.hasValue(item));
      }
      return this.hasValue(value);
    });
  }

  private normalizeId(value: unknown): string | number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return typeof value === 'number' ? value : String(value);
  }

  private applyNumericDefaults(entry: EquipmentMetadata): EquipmentMetadata {
    return {
      ...entry,
      time: entry.time ?? 0,
      quantity: entry.quantity ?? 0,
      unit: entry.unit ?? 0,
    };
  }

  private hasValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    return true;
  }

  private isNumericValue(value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return true;
    }

    const text = String(value).trim();
    if (text === '') {
      return true;
    }

    return /^[0-9]+(\.[0-9]+)?$/.test(text);
  }

  private asTrimmedString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private parseInteger(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? Math.trunc(num) : null;
  }

  private parseFloatValue(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? num : null;
  }
}
